(() => {
  const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
  const controllers = new Map();

  function sectionRoot(node) {
    return node?.matches?.('.shopify-section') ? node : node?.closest?.('.shopify-section');
  }

  function initSection(root = document) {
    const key = root.id || 'document';
    controllers.get(key)?.abort();
    const controller = new AbortController();
    controllers.set(key, controller);
    const { signal } = controller;

    const animatedTracks = [...root.querySelectorAll('.ticker__track, .planner-marquee__track')];
    if (animatedTracks.length && 'IntersectionObserver' in window) {
      const visibleTracks = new Set();
      const updateMotion = () => animatedTracks.forEach((track) => {
        track.style.animationPlayState = !document.hidden && visibleTracks.has(track) ? 'running' : 'paused';
      });
      const motionObserver = new IntersectionObserver((entries) => {
        entries.forEach(({ target, isIntersecting }) => {
          if (isIntersecting) visibleTracks.add(target);
          else visibleTracks.delete(target);
        });
        updateMotion();
      });
      animatedTracks.forEach((track) => motionObserver.observe(track));
      updateMotion();
      document.addEventListener('visibilitychange', updateMotion, { signal });
      signal.addEventListener('abort', () => {
        motionObserver.disconnect();
        animatedTracks.forEach((track) => track.style.removeProperty('animation-play-state'));
      }, { once: true });
    }

    const stickyHeader = root.querySelector('.site-header.is-sticky');
    if (stickyHeader) {
      const headerSection = stickyHeader.closest('.shopify-section');
      const announcement = headerSection?.querySelector('.announcement');
      const measureAnnouncement = () => headerSection?.style.setProperty('--announcement-height', `${announcement?.getBoundingClientRect().height || 0}px`);
      measureAnnouncement();
      if (announcement && 'ResizeObserver' in window) {
        const observer = new ResizeObserver(measureAnnouncement);
        observer.observe(announcement);
        signal.addEventListener('abort', () => observer.disconnect(), { once: true });
      }
    }

    const mobileMenu = root.querySelector('[data-mobile-menu]');
    if (mobileMenu) {
      const toggles = [...root.querySelectorAll('[data-mobile-menu-toggle]')];
      const dismissers = [...root.querySelectorAll('[data-mobile-menu-dismiss]')];
      let returnFocus = null;
      const setMenu = (open, trigger = null) => {
        mobileMenu.classList.toggle('is-open', open);
        mobileMenu.setAttribute('aria-hidden', String(!open));
        if (open) mobileMenu.removeAttribute('inert');
        else mobileMenu.setAttribute('inert', '');
        document.body.classList.toggle('menu-open', open);
        toggles.forEach((item) => item.setAttribute('aria-expanded', String(open)));
        if (open) {
          returnFocus = trigger;
          mobileMenu.querySelector('a,button')?.focus();
        } else if (returnFocus?.isConnected) {
          returnFocus.focus();
          returnFocus = null;
        }
      };
      setMenu(false);
      toggles.forEach((button) => button.addEventListener('click', () => setMenu(!mobileMenu.classList.contains('is-open'), button), { signal }));
      dismissers.forEach((button) => button.addEventListener('click', () => setMenu(false), { signal }));
      mobileMenu.querySelectorAll('a').forEach((link) => link.addEventListener('click', () => setMenu(false), { signal }));
      root.addEventListener('keydown', (event) => {
        if (event.key === 'Escape' && mobileMenu.classList.contains('is-open')) setMenu(false);
        if (event.key === 'Tab' && mobileMenu.classList.contains('is-open')) {
          const focusable = [...mobileMenu.querySelectorAll('a,button,summary,input')].filter(el => el.getClientRects().length);
          const first = focusable[0], last = focusable.at(-1);
          if (event.shiftKey && document.activeElement === first) { event.preventDefault(); last?.focus(); }
          else if (!event.shiftKey && document.activeElement === last) { event.preventDefault(); first?.focus(); }
        }
      }, { signal });
      window.addEventListener('resize', () => {
        if (window.innerWidth > 800 && mobileMenu.classList.contains('is-open')) setMenu(false);
      }, { signal });
    }

    root.querySelectorAll('[data-tabs]').forEach((component) => {
      const tabs = [...component.querySelectorAll('[role=tab]')];
      const panels = [...component.querySelectorAll('[role=tabpanel]')];
      const activate = (tab, focus = false) => {
        const target = tab.dataset.tab;
        tabs.forEach((item) => {
          const selected = item === tab;
          item.setAttribute('aria-selected', String(selected));
          item.tabIndex = selected ? 0 : -1;
        });
        panels.forEach((panel) => {
          const selected = panel.dataset.panel === target;
          panel.classList.toggle('is-active', selected);
          panel.hidden = !selected;
        });
        if (focus) tab.focus();
      };
      tabs.forEach((tab, index) => {
        tab.addEventListener('click', () => activate(tab), { signal });
        tab.addEventListener('keydown', (event) => {
          if (!['ArrowRight', 'ArrowLeft', 'ArrowDown', 'ArrowUp', 'Home', 'End'].includes(event.key)) return;
          event.preventDefault();
          let next = index;
          if (event.key === 'Home') next = 0;
          else if (event.key === 'End') next = tabs.length - 1;
          else if (['ArrowRight', 'ArrowDown'].includes(event.key)) next = (index + 1) % tabs.length;
          else next = (index - 1 + tabs.length) % tabs.length;
          activate(tabs[next], true);
        }, { signal });
      });
      const selected = tabs.find((tab) => tab.getAttribute('aria-selected') === 'true') || tabs[0];
      if (selected) activate(selected);
    });

    root.querySelectorAll('[data-planner-jump]').forEach((link) => {
      link.addEventListener('click', (event) => {
        const target = link.dataset.plannerJump;
        const host = link.closest('.shopify-section');
        const siblings = [...(host?.parentElement?.children || [])];
        const planners = siblings.slice(siblings.indexOf(host) + 1).map(item => item.querySelector('[data-home-planner-tabs]')).filter(Boolean);
        const planner = planners[0];
        const tab = planner?.querySelector(`[role=tab][data-target-key="${CSS.escape(target)}"]`);
        if (!tab) return;
        event.preventDefault();
        tab.click();
        planner.scrollIntoView({ behavior: reducedMotion.matches ? 'auto' : 'smooth', block: 'center' });
        window.setTimeout(() => tab.focus(), reducedMotion.matches ? 0 : 450);
      }, { signal });
    });

    root.querySelectorAll('[data-finder]').forEach((finder) => {
      const options = [...finder.querySelectorAll('[data-finder-option]')];
      const title = finder.querySelector('[data-finder-title]');
      const edition = finder.querySelector('[data-finder-edition]');
      const description = finder.querySelector('[data-finder-description]');
      const image = finder.querySelector('[data-finder-image]');
      const link = finder.querySelector('[data-finder-link]');
      const visual = finder.querySelector('[data-finder-visual]');
      const activate = (option) => {
        options.forEach((item) => item.setAttribute('aria-pressed', String(item === option)));
        if (title) title.textContent = option.dataset.title || '';
        const price = finder.querySelector('[data-finder-price]');
        if (price) price.textContent = option.dataset.price || '';
        if (edition) edition.textContent = option.dataset.edition || '';
        if (description) description.textContent = option.dataset.description || '';
        if (image && option.dataset.image) { image.removeAttribute('srcset'); image.src = option.dataset.image; }
        if (image) image.alt = option.dataset.imageAlt || '';
        if (link && option.dataset.url) link.href = option.dataset.url;
        if (visual) visual.style.setProperty('--finder-tone', option.dataset.tone || '#ebeae6');
      };
      options.forEach((option) => option.addEventListener('click', () => activate(option), { signal }));
      if (options[0]) activate(options.find((option) => option.getAttribute('aria-pressed') === 'true') || options[0]);
    });

    root.querySelectorAll('[data-product-gallery]').forEach((gallery) => {
      const images = [...gallery.querySelectorAll('[data-gallery-image]')];
      const thumbs = [...gallery.querySelectorAll('[data-gallery-thumb]')];
      const count = gallery.querySelector('[data-gallery-count]');
      let index = 0;
      const show = (next) => {
        index = (next + images.length) % images.length;
        images.forEach((image, itemIndex) => image.classList.toggle('is-active', itemIndex === index));
        thumbs.forEach((thumb, itemIndex) => thumb.setAttribute('aria-pressed', String(itemIndex === index)));
        if (count) count.textContent = `${index + 1} / ${images.length}`;
      };
      thumbs.forEach((thumb, itemIndex) => thumb.addEventListener('click', () => show(itemIndex), { signal }));
      gallery.querySelectorAll('[data-gallery-step]').forEach((button) => button.addEventListener('click', () => show(index + Number(button.dataset.galleryStep)), { signal }));
      let startX = null;
      gallery.addEventListener('touchstart', (event) => { startX = event.touches[0]?.clientX ?? null; }, { signal, passive: true });
      gallery.addEventListener('touchend', (event) => {
        if (startX === null) return;
        const delta = (event.changedTouches[0]?.clientX ?? startX) - startX;
        if (Math.abs(delta) > 45) show(index + (delta < 0 ? 1 : -1));
        startX = null;
      }, { signal, passive: true });
      gallery.showMedia = (mediaId) => {
        const next = images.findIndex((image) => image.dataset.mediaId === String(mediaId));
        if (next >= 0) show(next);
      };
      show(0);
    });

    root.querySelectorAll('[data-variant-select]').forEach((select) => {
      const updateVariant = () => {
        const option = select.selectedOptions[0];
        const section = select.closest('.main-product');
        const price = section?.querySelector('.product-price');
        const addButton = section?.querySelector('[data-variant-add]');
        const available = option?.dataset.available === 'true';
        if (price && option?.dataset.price) price.textContent = option.dataset.price;
        if (addButton) {
          addButton.disabled = !available;
          addButton.textContent = available ? addButton.dataset.availableLabel : addButton.dataset.soldOutLabel;
        }
        if (option?.dataset.mediaId) section?.querySelector('[data-product-gallery]')?.showMedia?.(option.dataset.mediaId);
      };
      select.addEventListener('change', updateVariant, { signal });
      updateVariant();
    });

    root.querySelectorAll('[data-quantity]').forEach((control) => {
      const input = control.querySelector('input');
      control.querySelectorAll('[data-quantity-step]').forEach((button) => {
        button.addEventListener('click', () => {
          const value = Math.max(Number(input.min || 1), Math.min(Number(input.max || 99), Number(input.value || 1) + Number(button.dataset.quantityStep)));
          input.value = value;
          input.dispatchEvent(new Event('change', { bubbles: true }));
        }, { signal });
      });
    });


    root.querySelectorAll('.align-product-form').forEach(form => {
      form.addEventListener('submit', async event => {
        event.preventDefault();
        const button = form.querySelector('[name="add"]');
        if (button.disabled) return;
        const feedback = form.querySelector('.align-cart-feedback');
        button.disabled = true; button.setAttribute('aria-busy','true');
        const originalLabel = button.textContent;
        try {
          if (window.AlignCartDrawer) {
            await window.AlignCartDrawer.add(form, button);
            feedback.hidden = true;
          } else {
            HTMLFormElement.prototype.submit.call(form);
          }
        } catch(error) { feedback.hidden = false; feedback.textContent = error.message; }
        finally {button.disabled = false;button.removeAttribute('aria-busy');button.textContent = originalLabel;}
      }, {signal});
    });

    root.querySelectorAll('[data-auto-submit]').forEach((control) => {
      control.addEventListener('change', () => control.form?.requestSubmit(), { signal });
    });

    root.querySelectorAll('[data-typing]').forEach((element) => {
      const full = element.dataset.text || '';
      const note = element.closest('.align-typing-note');
      const caret = note?.querySelector('.type-caret');
      const enabled = element.dataset.enabled === 'true' && !reducedMotion.matches;
      let timer, observer, started = false;
      const complete = () => {
        window.clearTimeout(timer);
        element.textContent = full;
        element.dataset.typingState = 'complete';
        caret?.classList.add('complete');
        observer?.disconnect();
      };
      signal.addEventListener('abort', () => {window.clearTimeout(timer);observer?.disconnect();}, {once:true});
      if (!enabled || !('IntersectionObserver' in window)) {complete();return;}
      element.textContent = '';
      element.dataset.typingState = 'waiting';
      caret?.classList.remove('complete');
      const chars = Array.from(full);
      const scale = Number(element.dataset.speed || 68) / 68;
      let index = 0;
      const type = () => {
        if(signal.aborted) return;
        element.textContent = chars.slice(0, ++index).join('');
        if(index < chars.length) {
          const last = chars[index - 1];
          timer = window.setTimeout(type, (last === '.' ? 520 : last === ',' ? 330 : last === ' ' ? 55 : 68) * scale);
        } else {
          element.dataset.typingState = 'complete';
          timer = window.setTimeout(() => caret?.classList.add('complete'), 1800);
        }
      };
      observer = new IntersectionObserver(entries => {
        if(started || !entries.some(entry => entry.isIntersecting)) return;
        started = true;observer.disconnect();element.dataset.typingState = 'typing';
        timer = window.setTimeout(type, Number(element.dataset.delay || 420));
      }, {threshold:.33});
      observer.observe(note || element);
      reducedMotion.addEventListener('change', event => {if(event.matches) complete();}, {signal});
    });

    const reveal = [...root.querySelectorAll('.reveal')];
    if (reveal.length && !reducedMotion.matches && !document.body.classList.contains('motion-disabled') && !window.Shopify?.designMode) {
      const observer = new IntersectionObserver((entries) => entries.forEach((entry) => {
        if (entry.isIntersecting) { entry.target.classList.add('is-visible'); observer.unobserve(entry.target); }
      }), { rootMargin: '0px 0px -7% 0px' });
      reveal.forEach((item) => observer.observe(item));
      signal.addEventListener('abort', () => observer.disconnect(), { once: true });
    } else reveal.forEach((item) => item.classList.add('is-visible'));

    root.querySelectorAll('[data-gallery-zoom]').forEach((button) => {
      button.addEventListener('click', () => {
        const dialog = root.querySelector('[data-zoom-dialog]') || document.querySelector('[data-zoom-dialog]');
        const image = dialog?.querySelector('[data-zoom-image]');
        if (!dialog || !image) return;
        image.src = button.dataset.zoomSrc;
        image.alt = button.dataset.zoomAlt || '';
        dialog.showModal();
      }, { signal });
    });
    root.querySelectorAll('[data-current-zoom]').forEach((button) => button.addEventListener('click', () => button.closest('.main-product')?.querySelector('.product-gallery__image.is-active [data-gallery-zoom]')?.click(), { signal }));
    root.querySelectorAll('[data-zoom-close]').forEach((button) => button.addEventListener('click', () => button.closest('dialog')?.close(), { signal }));
  }

  function rootTab(panel) {
    const component = panel.closest('[data-tabs]');
    component?.querySelector(`[aria-controls="${CSS.escape(panel.id)}"]`)?.click();
  }
  const init = () => document.querySelectorAll('.shopify-section').forEach((section) => initSection(section));
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init, { once: true }); else init();
  document.addEventListener('shopify:section:load', (event) => initSection(sectionRoot(event.target) || event.target));
  document.addEventListener('shopify:section:unload', (event) => controllers.get(event.target.id)?.abort());
  document.addEventListener('shopify:block:select', (event) => {
    const block = event.target;
    const tab = block.matches?.('[role=tab]') ? block : block.querySelector('[role=tab]');
    const panel = block.matches?.('[role=tabpanel]') ? block : block.closest('[role=tabpanel]');
    const details = block.matches?.('details') ? block : block.querySelector('details');
    tab?.click();
    if (panel) rootTab(panel);
    block.querySelector('[data-finder-option]')?.click();
    if (block.matches?.('[data-finder-option]')) block.click();
    block.querySelector('[data-planner-jump]')?.click();
    details?.setAttribute('open', '');
  });
})();
